import { useTranslation } from "react-i18next";
import {
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "frontend-components";
import { createColumnHelper, flexRender, type ColumnDef } from "@tanstack/react-table";
import {
    faArrowDownWideShort,
    faArrowUpWideShort,
    faLink,
} from "@fortawesome/pro-regular-svg-icons";
import { faTrashCan, faTriangleExclamation } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useMemo, useState } from "react";
import { useDeleteTask } from "@dashboard/hooks/useTaskQuery";
import { ConfirmDeleteDialog } from "@dashboard/components/common/ConfirmDeleteDialog";
import Pagination from "./Pagination";
import { useAnnualCycleTable, type AnnualCycleFilters } from "@dashboard/hooks/useAnnualCycleTable";
import AnnualCycleToolbar from "./AnnualCycleToolbar";
import AnnualCycleCard from "./AnnualCycleCard";
import { useNavigate } from "@tanstack/react-router";
import { useTasks } from "@dashboard/hooks/useTaskQuery";
import {
    pickLatestOccurrence,
    withOccurrence,
    type TaskWithOccurrence,
} from "@dashboard/utils/taskWithOccurrence";
import { useQuery } from "@tanstack/react-query";
import { taskOccurrencesOptions } from "@dashboard/api/queryApi";
import { TaskStatus, TaskStatusSelect } from "@dashboard/components/TaskStatusSelect";
import { useHseInstance } from "@dashboard/hooks/useHseInstance";
import { Can } from "@dashboard/casl/taskAbility";
import { subject } from "@casl/ability";
import { formatDateShort } from "@dashboard/utils/formatDate";

type AnnualCycleTableProps = {
    filters: AnnualCycleFilters;
    onFilterChange: <K extends keyof AnnualCycleFilters>(
        key: K,
        value: AnnualCycleFilters[K],
    ) => void;
    onClearFilters: () => void;
    hasFilter: <K extends keyof AnnualCycleFilters>(key: K) => boolean;
    hasAnyFilter: () => boolean;
};

export default function AnnualCycleTable({
    filters,
    onFilterChange,
    onClearFilters,
    hasFilter,
    hasAnyFilter,
}: AnnualCycleTableProps) {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const deleteMutation = useDeleteTask();
    const [taskToDelete, setTaskToDelete] = useState<number | null>(null);
    const { instanceId } = useHseInstance();

    const {
        data: tasksResponse,
        isLoading: isLoadingTasks,
        error,
    } = useTasks({
        status: "ACTIVE",
        take: 50,
        annualCycle: true,
    });

    const { data: occurrencesResponse, isLoading: isLoadingOccurrences } = useQuery(
        taskOccurrencesOptions({
            instanceId,
            take: 100,
        }),
    );

    const isLoading = isLoadingTasks || isLoadingOccurrences;

    const tableData = useMemo(() => {
        if (isLoading) {
            return Array(15).fill({} as TaskWithOccurrence);
        }

        if (!tasksResponse?.data) {
            return [];
        }

        const occurrencesByTask = new Map<
            number,
            NonNullable<typeof occurrencesResponse>["data"][0][]
        >();
        if (occurrencesResponse?.data) {
            occurrencesResponse.data.forEach((occurrence) => {
                const list = occurrencesByTask.get(occurrence.taskId) ?? [];
                list.push(occurrence);
                occurrencesByTask.set(occurrence.taskId, list);
            });
        }

        return tasksResponse.data.map((task) => {
            const occurrence = pickLatestOccurrence(occurrencesByTask.get(task.id));
            return withOccurrence(task, occurrence);
        });
    }, [tasksResponse, occurrencesResponse, isLoading]);

    const openTask = useCallback(
        (taskId: number) => {
            void navigate({
                to: ".",
                search: (prev) => ({ ...prev, task: taskId, edit: undefined }),
            });
        },
        [navigate],
    );

    const columns = useMemo(() => {
        const columnHelper = createColumnHelper<TaskWithOccurrence>();
        return [
            columnHelper.accessor("title", {
                header: () => t("common.activities"),
                cell: (info) => {
                    const task = info.row.original;
                    return (
                        <button
                            type="button"
                            className="hover:underline cursor-pointer text-left text-sm font-medium text-compendia-foreground"
                            onClick={() => openTask(task.id)}
                        >
                            {info.getValue<string>()}
                        </button>
                    );
                },
                meta: { className: "w-auto" },
            }),
            columnHelper.display({
                id: "status",
                header: () => t("common.status"),
                cell: (info) => {
                    const task = info.row.original;
                    if (task.occurrence) {
                        return (
                            <Can
                                I="update"
                                this={subject("TaskOccurrence", { task: info.row.original })}
                                passThrough
                            >
                                {(allowed) =>
                                    allowed ? (
                                        <TaskStatusSelect
                                            taskOccurrenceId={task.occurrence!.id}
                                            currentStatus={task.occurrence!.status}
                                        />
                                    ) : (
                                        <TaskStatus status={task.occurrence!.status} />
                                    )
                                }
                            </Can>
                        );
                    }
                    return <TaskStatus status="OPEN" />;
                },
                meta: { className: "w-40" },
            }),
            columnHelper.display({
                id: "relatedLinks",
                header: () => t("common.relatedLinks"),
                cell: (info) => {
                    const task = info.row.original;
                    const links = task.links ?? [];
                    if (links.length === 0) return null;
                    const first = links[0];
                    return (
                        <div className="flex items-center gap-1">
                            <FontAwesomeIcon icon={faLink} />
                            <a
                                href={first.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="hover:underline text-sm truncate max-w-32 text-text-secondary"
                                onClick={(e) => e.stopPropagation()}
                            >
                                {first.title}
                            </a>
                            {links.length > 1 && (
                                <span className="text-xs bg-compendia-greige text-compendia-foreground rounded-full px-1.5 py-0.5 shrink-0">
                                    +{links.length - 1}
                                </span>
                            )}
                        </div>
                    );
                },
                meta: { className: "w-40" },
            }),
            columnHelper.accessor("ownerUserId", {
                header: () => t("common.responsible"),
                cell: (info) => {
                    const owner = info.row.original.ownerUser;
                    return owner ? (
                        <span className="truncate">
                            {owner.firstName} {owner.lastName}
                        </span>
                    ) : null;
                },
                meta: {
                    className: "w-44",
                    cellClassName: "text-label text-compendia-foreground",
                },
            }),
            columnHelper.accessor("dueDate", {
                id: "dueDate",
                header: () => t("common.dueDate"),
                cell: (info) => {
                    const task = info.row.original;
                    const dateStr = formatDateShort(info.getValue<Date>(), i18n.language);
                    if (task.isExpired) {
                        return (
                            <span className="flex items-center gap-1 text-chameleon-pink font-medium">
                                {dateStr}
                                <FontAwesomeIcon icon={faTriangleExclamation} />
                            </span>
                        );
                    }
                    return dateStr;
                },
                filterFn: (row, columnId, filterValue: { months: number[]; year?: number }) => {
                    const dueDate = row.getValue(columnId) as Date;
                    if (!dueDate) return false;
                    if (filterValue.year && dueDate.getFullYear() !== filterValue.year) {
                        return false;
                    }
                    if (filterValue.months && filterValue.months.length > 0) {
                        return filterValue.months.includes(dueDate.getMonth());
                    }
                    return true;
                },
                meta: {
                    className: "w-32",
                    cellClassName: "text-label text-compendia-muted",
                },
            }),
            columnHelper.display({
                id: "delete",
                header: () => null,
                cell: (info) => (
                    <Can
                        I="delete"
                        this={subject("Task", {
                            instanceId,
                            createdById: info.row.original.createdById,
                        })}
                    >
                        <button
                            type="button"
                            aria-label={t("common.deleteActivity")}
                            className="cursor-pointer w-full"
                            onClick={(e) => {
                                e.stopPropagation();
                                setTaskToDelete(info.row.original.id);
                            }}
                        >
                            <FontAwesomeIcon icon={faTrashCan} />
                        </button>
                    </Can>
                ),
                size: 40,
                meta: { className: "w-12" },
            }),
        ] as ColumnDef<TaskWithOccurrence>[];
    }, [t, i18n.language, instanceId, openTask]);

    const tableColumns = useMemo(
        () =>
            isLoading
                ? columns.map((column) => ({
                      ...column,
                      cell: () => (
                          <div className="w-full bg-compendia-foreground/30 h-5 rounded-full animate-pulse" />
                      ),
                  }))
                : columns,
        [columns, isLoading],
    );

    const table = useAnnualCycleTable(tableData, tableColumns, filters);
    const rows = table.getRowModel().rows;
    const dueDateColumn = table.getColumn("dueDate");
    const sortDirection = dueDateColumn?.getIsSorted();

    return (
        <>
            <AnnualCycleToolbar
                filters={filters}
                onFilterChange={onFilterChange}
                onClearFilters={onClearFilters}
                hasFilter={hasFilter}
                hasAnyFilter={hasAnyFilter}
            />

            {error && (
                <div className="text-chameleon-pink p-4 bg-chameleon-pink/10 rounded-[3px] border border-chameleon-pink/30">
                    {t("common.errorLoadingTasks")}: {error.message}
                </div>
            )}

            <div className="flex flex-col gap-3 xl:hidden">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <span className="text-category font-medium uppercase tracking-category text-compendia-muted">
                        {t("common.activities")}
                    </span>
                    <div className="flex items-center gap-4">
                        <button
                            type="button"
                            onClick={() => dueDateColumn?.toggleSorting(sortDirection === "asc")}
                            className="flex cursor-pointer items-center gap-1.5 text-label text-compendia-muted transition-colors hover:text-chameleon-green"
                        >
                            {t("common.dueDate")}
                            <FontAwesomeIcon
                                icon={
                                    sortDirection === "asc"
                                        ? faArrowUpWideShort
                                        : faArrowDownWideShort
                                }
                            />
                        </button>
                        <span className="text-label text-compendia-muted">
                            {t("common.total")}:{" "}
                            <span className="font-semibold text-compendia-foreground">
                                {table.getFilteredRowModel().rows.length}
                            </span>
                        </span>
                    </div>
                </div>

                {isLoading ? (
                    Array.from({ length: 5 }, (_, idx) => (
                        <Skeleton key={idx} className="h-[88px] rounded-[3px]" />
                    ))
                ) : rows.length ? (
                    <ul className="flex flex-col gap-3">
                        {rows.map((row) => (
                            <AnnualCycleCard key={row.id} task={row.original} onOpen={openTask} />
                        ))}
                    </ul>
                ) : (
                    <div className="rounded-[3px] border border-compendia-border bg-compendia-card py-8 text-center text-compendia-muted">
                        {t("common.no_results")}
                    </div>
                )}
            </div>

            <div className="hidden rounded-[3px] border border-compendia-border overflow-hidden bg-compendia-card xl:block">
                <Table className="w-full border-collapse">
                    <TableHeader className="bg-compendia-greige-10">
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow
                                key={headerGroup.id}
                                className="border-b border-compendia-border hover:bg-transparent"
                            >
                                {headerGroup.headers.map((header) => {
                                    const headerMeta = header.column.columnDef.meta as
                                        | { className?: string }
                                        | undefined;
                                    return (
                                        <TableHead
                                            key={header.id}
                                            className={`text-compendia-foreground text-xs font-semibold uppercase tracking-[0.06em] ${
                                                headerMeta?.className ?? ""
                                            }`}
                                        >
                                            {header.column.getCanSort() ? (
                                                <button
                                                    type="button"
                                                    className="w-full flex items-center hover:text-chameleon-green transition-colors"
                                                    onClick={header.column.getToggleSortingHandler()}
                                                >
                                                    {header.isPlaceholder
                                                        ? null
                                                        : flexRender(
                                                              header.column.columnDef.header,
                                                              header.getContext(),
                                                          )}
                                                    {header.column.getIsSorted() === "desc" && (
                                                        <FontAwesomeIcon
                                                            className="pl-1"
                                                            icon={faArrowDownWideShort}
                                                        />
                                                    )}
                                                    {header.column.getIsSorted() === "asc" && (
                                                        <FontAwesomeIcon
                                                            className="pl-1"
                                                            icon={faArrowUpWideShort}
                                                        />
                                                    )}
                                                </button>
                                            ) : header.isPlaceholder ? null : (
                                                flexRender(
                                                    header.column.columnDef.header,
                                                    header.getContext(),
                                                )
                                            )}
                                        </TableHead>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody>
                        {table.getRowModel().rows.length ? (
                            table.getRowModel().rows.map((row) => (
                                <TableRow
                                    key={row.id}
                                    className="border-b border-compendia-border h-14 hover:bg-compendia-greige-10 transition-colors last:border-b-0"
                                >
                                    {row.getVisibleCells().map((cell, cellIndex) => {
                                        const cellMeta = cell.column.columnDef.meta as
                                            | { className?: string; cellClassName?: string }
                                            | undefined;
                                        const widthClass = cellMeta?.className ?? "";
                                        const cellTextClass = cellMeta?.cellClassName ?? "";
                                        const baseClass =
                                            cellIndex === 0 && row.original.isExpired
                                                ? "border-l-4 border-l-chameleon-pink"
                                                : "";
                                        return (
                                            <TableCell
                                                key={cell.id}
                                                className={`${baseClass} ${widthClass} ${cellTextClass}`}
                                            >
                                                {flexRender(
                                                    cell.column.columnDef.cell,
                                                    cell.getContext(),
                                                )}
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell
                                    colSpan={table.getAllColumns().length}
                                    className="text-center text-compendia-muted py-8"
                                >
                                    {t("common.no_results")}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>
            <Pagination table={table} />
            <ConfirmDeleteDialog
                open={taskToDelete !== null}
                onOpenChange={(open) => {
                    if (!open) setTaskToDelete(null);
                }}
                onConfirm={async () => {
                    if (taskToDelete !== null) await deleteMutation.mutateAsync(taskToDelete);
                }}
                title={t("common.deleteActivity")}
                description={t("common.confirmDeleteActivity")}
            />
        </>
    );
}
